import { HttpErrorResponse } from '@angular/common/http';

export const API_RATE_LIMIT_ERROR_CODE = 'API_RATE_LIMIT' as const;

export const API_RATE_LIMIT_MESSAGE = 'Too many requests. Please wait and try again.';

export function isRateLimitHttpStatus(status: number | undefined | null): boolean {
  return status === 429;
}

export function isRateLimitErrorCode(code: string | undefined | null): boolean {
  return code?.trim() === API_RATE_LIMIT_ERROR_CODE;
}

export function isRateLimitError(
  err: { code?: string | null; status?: number | null } | null | undefined,
): boolean {
  return isRateLimitHttpStatus(err?.status) || isRateLimitErrorCode(err?.code);
}

export function parseRetryAfterMs(headers: Headers | HttpErrorResponse['headers'] | null | undefined): number {
  if (!headers) {
    return 60_000;
  }
  const raw =
    headers instanceof Headers
      ? headers.get('Retry-After')
      : headers.get?.('Retry-After') ?? headers.get?.('retry-after');
  const value = raw != null ? String(raw).trim() : '';
  if (!value) {
    return 60_000;
  }
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds > 0) {
    return Math.ceil(seconds * 1000);
  }
  const dateMs = Date.parse(value);
  if (Number.isFinite(dateMs)) {
    return Math.max(1000, dateMs - Date.now());
  }
  return 60_000;
}

/** Generic copy for status 0 (network down / CORS / cross-origin session redirect). */
export const CONNECTION_ERROR_MESSAGE =
  'Unable to reach the server. Please check your connection and try again.';

/** True when a string body looks like an HTML document (e.g. an ATS login page), not an API message. */
function looksLikeHtmlBody(body: string): boolean {
  return /^\s*<(?:!doctype|html|head|body)\b/i.test(body);
}

export function extractHttpErrorMessage(err: unknown, fallback = ''): string {
  if (err instanceof HttpErrorResponse) {
    const body = err.error;
    if (body && typeof body === 'object' && !Array.isArray(body)) {
      const message = (body as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) {
        return message.trim();
      }
      if (Array.isArray(message)) {
        const first = message.find((item) => typeof item === 'string' && item.trim());
        if (typeof first === 'string' && first.trim()) {
          return first.trim();
        }
      }
      const code = (body as { code?: unknown }).code;
      if (typeof code === 'string' && isRateLimitErrorCode(code)) {
        return API_RATE_LIMIT_MESSAGE;
      }
    }
    if (isRateLimitHttpStatus(err.status)) {
      return API_RATE_LIMIT_MESSAGE;
    }
    if (typeof body === 'string' && body.trim() && !looksLikeHtmlBody(body)) {
      return body.trim();
    }
    // Never fall through to `err.message` for an HttpErrorResponse — Angular's
    // default (e.g. "Http failure response for {url}: 0 Unknown Error") leaks
    // the endpoint URL into the UI.
    if (err.status === 0) {
      return fallback || CONNECTION_ERROR_MESSAGE;
    }
    return fallback || `Request failed (${err.status}).`;
  }
  if (err && typeof err === 'object' && 'message' in err) {
    const message = String((err as { message?: string }).message ?? '').trim();
    if (message) {
      return message;
    }
  }
  return fallback;
}

export function parseFetchErrorPayload(
  rawText: string,
  httpStatus?: number,
): { message: string; code?: string } {
  const fallback = {
    message: isRateLimitHttpStatus(httpStatus) ? API_RATE_LIMIT_MESSAGE : rawText?.trim() || (httpStatus ? `HTTP ${httpStatus}` : 'Request failed'),
    code: isRateLimitHttpStatus(httpStatus) ? API_RATE_LIMIT_ERROR_CODE : undefined,
  };
  if (!rawText?.trim()) {
    return fallback;
  }
  try {
    const body = JSON.parse(rawText.trim()) as Record<string, unknown>;
    const rawCode = typeof body['code'] === 'string' ? body['code'].trim() : undefined;
    const code = isRateLimitErrorCode(rawCode) ? API_RATE_LIMIT_ERROR_CODE : rawCode ?? fallback.code;
    const message =
      typeof body['message'] === 'string' && body['message'].trim()
        ? isRateLimitErrorCode(rawCode)
          ? API_RATE_LIMIT_MESSAGE
          : body['message'].trim()
        : fallback.message;
    return { message, code };
  } catch {
    return fallback;
  }
}
